import { useContext, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { CoreContext } from "../../context/global/CoreContext";

import { IoIosArrowRoundBack } from "react-icons/io";

import RSLogo from "../icons/RSLogo";
import Button from "../ui/buttons/Button";
import { ConfirmModal } from "../ui";
import UserPortal from "./UserPortal";
import { getCookie } from "../../utils/dom";
import { getUserDisplayName } from "../../utils/business";

/**
 * Fixed top navigation shell for the authenticated React app: back, home (logo),
 * and logout. Sticks to the top of the viewport when scrolling.
 *
 * @returns {JSX.Element}
 */
const NavBar = () => {
  const { baseURL, debug, user, impersonation, refreshUser } = useContext(CoreContext);
  const navigate = useNavigate();
  const location = useLocation();
  const [isLogoutModalOpen, setIsLogoutModalOpen] = useState(false);
  const isImpersonating = Boolean(impersonation?.active);
  const isDebugMode = Boolean(debug);
  const shouldShowUserPortal = Boolean(user?.executive || isImpersonating);
  const impersonatedUserName = getUserDisplayName(
    impersonation?.impersonated_user || user
  );
  const logoutModalText = user?.email
    ? `Are you sure you want to log out of the account ${user.email}?`
    : "Are you sure you want to log out of this account?";
  // React Router history entries expose `idx` (0 = first entry / no in-app back).
  // Fall back to location.key for environments without idx. Reading `location`
  // re-evaluates this after in-app navigations.
  const historyIdx = window.history.state?.idx;
  const canGoBack =
    typeof historyIdx === "number"
      ? historyIdx > 0
      : location.key !== "default";

  /**
   * Navigates back one history entry when in-app history exists.
   *
   * @returns {void}
   */
  const handleBack = () => {
    if (!canGoBack) {
      return;
    }
    navigate(-1);
  };

  /**
   * Client-side navigates to home on primary click; leaves new-tab gestures native.
   *
   * @param {React.MouseEvent} event Click event on the logo link.
   * @returns {void}
   */
  const handleHomeClick = (event) => {
    // Keep middle-click/Ctrl-click native so the homepage can open in a new tab.
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey) {
      return;
    }
    event.preventDefault();
    navigate("/home");
  };

  /**
   * Opens the logout confirmation modal.
   *
   * @returns {void}
   */
  const handleLogout = () => {
    setIsLogoutModalOpen(true);
  };

  /**
   * Posts a CSRF-backed logout form to Django allauth.
   *
   * @returns {void}
   */
  const handleConfirmLogout = () => {
    const form = document.createElement("form");
    form.method = "post";
    form.action = `${baseURL}accounts/logout/`;

    const csrfInput = document.createElement("input");
    csrfInput.type = "hidden";
    csrfInput.name = "csrfmiddlewaretoken";
    csrfInput.value = getCookie("csrftoken");

    form.appendChild(csrfInput);
    document.body.appendChild(form);
    form.submit();
  };

  const navbarBackgroundClass = isDebugMode
    ? "bg-red-900"
    : isImpersonating
      ? "bg-yellow-600"
      : "bg-primary";

  return (
    <>
      <div
        className={`navbar h-14 w-full font-inter caret-transparent flex flex-row justify-between items-center text-white sticky top-0 z-50 shadow-lg shadow-primary-900/20 ${navbarBackgroundClass}`}
      >
        <button
          type="button"
          onClick={handleBack}
          disabled={!canGoBack}
          aria-label="Back"
          title={canGoBack ? "Back" : "No previous page"}
          className={`relative z-10 ml-2 flex flex-row items-center rounded-full pr-4 pl-2 py-1 transition ${
            canGoBack
              ? "cursor-pointer group/back text-white/85 hover:bg-white/10 hover:text-white"
              : "cursor-not-allowed text-white/35"
          }`}
        >
          <IoIosArrowRoundBack
            size={24}
            className={canGoBack ? "group-hover/back:scale-125" : undefined}
          />
          <p className="text-sm ml-1">Back</p>
        </button>
        <div className="pointer-events-none absolute left-1/2 top-1/2 z-0 max-w-[calc(100%-10rem)] -translate-x-1/2 -translate-y-1/2">
          {isImpersonating ? (
            <a className="pointer-events-auto truncate text-center font-bold text-white" href="/app/home" title="Return to Homepage">
              Impersonating{" "}
              <span className="text-green-900">{impersonatedUserName}</span>
            </a>
          ) : (
            <a
              href="/app/home"
              title="Return to Homepage"
              className="pointer-events-auto flex items-center justify-center"
              onClick={handleHomeClick}
            >
              <div className="flex flex-row items-center transition-all ease-in-out">
                <RSLogo classes="h-8" variant={"logo_trans_white"} />
                {isDebugMode ? (
                  <p className="ml-2 text-lg font-bold">DEBUG = TRUE</p>
                ) : (
                  <>
                    {/* For some reason the remaining MUI implementation of the cost page messes with the Tailwind breakpoints
          so the hide/unhide classes below are applied conditionally on the href, just for that page */}
                    <p
                      className={`ml-2 text-lg font-bold ${window.location.href.includes("cost") ? "" : "hidden sm:flex"}`}
                    >
                      R&S CONSTRUCTION
                    </p>
                    <p
                      className={`ml-2 text-lg font-bold ${window.location.href.includes("cost") ? "hidden" : "flex sm:hidden"}`}
                    >
                      R&S
                    </p>
                  </>
                )}
              </div>
            </a>
          )}
        </div>
        <div className="relative z-10 mr-2 text-sm">
          {shouldShowUserPortal ? (
            <UserPortal
              user={user}
              impersonation={impersonation}
              baseURL={baseURL}
              refreshUser={refreshUser}
              onLogout={handleLogout}
            />
          ) : baseURL ? (
            <Button onClick={handleLogout} text="Logout" variant="navPill" />
          ) : (
            <Button text="Logout" variant="navPill" />
          )}
        </div>
      </div>
      <ConfirmModal
        isOpen={isLogoutModalOpen}
        title="Log Out?"
        text={logoutModalText}
        buttonText="Log Out"
        onConfirm={handleConfirmLogout}
        onClose={() => setIsLogoutModalOpen(false)}
      />
    </>
  );
};

export default NavBar;
